import { Card } from "@material-ui/core";
import { tokens } from "app/theme";
import useCapacity from "capacity/useCapacity";
import { InlineCountCard } from "components/InlineCountCard";
import { useEMLeads } from "ems/useEMLeads";
import { useEngineers } from "engineers/useEngineers";
import { NeedsAttentionCard } from "notifications/NeedsAttentionCard";
import useRegions from "regions/useRegions";
import useSkills from "skills/useSkills";
import { CountCard } from "../../components/CountCard";
import useCustomers from "../../customers/useCustomers";
import { useEMs } from "../../ems/useEMs";
import { useLeads } from "../../leads/useLeads";
import { useManagers } from "../../managers/useManagers";
import { useTeams } from "../../teams/useTeams";
import { CustomersTrendsCard } from "../customers/CustomersTrendCard";
import { EngineersTrendsCard } from "../engineers/EngineersTrendCard";
import { CapacityByRegionCard } from "../regions/CapacityByRegionCard";
import { useDashboardPeriod } from "../useDashboardPeriod";
import { AllCustomersBurnedBudgetScatterChartCard } from "../utilization/BurnedBudgetByCustomerScatterChartCard";
import { BurnedBudgetCard } from "../utilization/BurnedBudgetCard";
import { CapacityByRoleCard } from "../utilization/CapacityByRoleCard";
import { HarvestBudgetTrend } from "../utilization/HarvestBudgetTrend";
import { UtilizationRatioCard } from "../utilization/UtilizationRatioCard";
import { UtilizationTrendsCard } from "../utilization/UtilizationTrendCard";

const SECTION_GAP = 14;

export function OverviewDashboardPage() {
  const period = useDashboardPeriod();
  const previousPeriod = useDashboardPeriod(-1);
  const regions = useRegions();
  const teams = useTeams(period.year, period.month);
  const ems = useEMs(period.year, period.month);
  const emLeads = useEMLeads(period.year, period.month);
  const capacities = useCapacity(period.year, period.month);
  const customers = useCustomers(period.year, period.month);
  const engineers = useEngineers(period.year, period.month);
  const managers = useManagers();
  const leads = useLeads(period.year, period.month);
  const skills = useSkills();

  const teamCount = Object.keys(teams).length;
  const engineerCount = Object.keys(capacities.items).length;
  const customerCount = customers.getAll().length;

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 14,
          marginBottom: 18,
        }}
      >
        <div>
          <h1
            style={{
              font: `600 22px/1.15 ${tokens.fontSans}`,
              letterSpacing: "-0.01em",
              color: tokens.fg1,
              margin: 0,
            }}
          >
            Fleet capacity
          </h1>
          <div
            style={{
              font: `400 12px/1.4 ${tokens.fontMono}`,
              color: tokens.fg3,
              marginTop: 5,
            }}
          >
            {teamCount} teams · {engineerCount} engineers · {customerCount}{" "}
            customers
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: SECTION_GAP,
        }}
      >
        <CountCard title="Teams" count={teamCount} path="/dashboards/teams" />
        <CountCard
          title="Engineers"
          count={engineerCount}
          path="/dashboards/engineers"
        />
        <CountCard
          title="Customers"
          count={customerCount}
          path="/dashboards/customers"
        />
        <CountCard
          title="Project Managers"
          count={Object.values(ems).length}
          path="/dashboards/ems"
        />
      </div>

      <Card
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          marginTop: 12,
          padding: "11px 14px",
          borderRadius: 12,
        }}
      >
        {[
          {
            title: "Managers",
            count: managers.length,
            path: "/dashboards/managers",
          },
          {
            title: "PM Leads",
            count: Object.keys(emLeads).length,
            path: "/dashboards/emleads",
          },
          { title: "Leads", count: leads.length, path: "/dashboards/leads" },
          {
            title: "Regions",
            count: Object.values(regions).length,
            path: "/dashboards/regions",
          },
          { title: "Skills", count: skills.length, path: "/dashboards/skills" },
        ].map((item) => (
          <div
            key={item.title}
            style={{
              paddingRight: 16,
              borderRight: `1px solid ${tokens.hairline}`,
            }}
          >
            <InlineCountCard
              title={item.title}
              count={item.count}
              path={item.path}
            />
          </div>
        ))}
      </Card>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr",
          gap: SECTION_GAP,
          marginTop: SECTION_GAP,
          alignItems: "start",
        }}
      >
        <NeedsAttentionCard
          year={period.year}
          month={period.month}
          teams={teams}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: SECTION_GAP,
          }}
        >
          <BurnedBudgetCard
            year={period.year}
            month={period.month}
            customers={customers.items}
          />
          <UtilizationRatioCard
            year={period.year}
            month={period.month}
            customers={customers.items || {}}
            engineers={engineers || {}}
          />
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr",
          gap: SECTION_GAP,
          marginTop: SECTION_GAP,
          alignItems: "start",
        }}
      >
        <AllCustomersBurnedBudgetScatterChartCard
          year={period.year}
          month={period.month}
          linkPrefix="/dashboards/customers/items"
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: SECTION_GAP,
          }}
        >
          <HarvestBudgetTrend period={previousPeriod} height={110} />
          <UtilizationTrendsCard period={period} height={110} />
          <CustomersTrendsCard period={period} height={110} />
          <EngineersTrendsCard period={period} height={110} />
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: SECTION_GAP,
          marginTop: SECTION_GAP,
          alignItems: "start",
        }}
      >
        <CapacityByRegionCard year={period.year} month={period.month} />
        <CapacityByRoleCard period={period} />
      </div>
    </div>
  );
}
